import {BehaviorSubject, merge, Subject} from 'rxjs';
import {map, mergeAll, scan, withLatestFrom} from 'rxjs/operators';
import {QtyType} from '@/enum/order';
import {numUtil} from '@/helper/num';
import {orderUtil} from '@/helper/order';
import {qtyUtil} from '@/helper/qty-type-contract';
import {contracts$} from '@/config';
import {
  incrementalOrders$,
  incrementalOrdersV2$,
  snapshotOrders$,
  snapshotOrdersV2$,
} from '@/orders-contract/orders-contract';
import {
  incrementalOrders$ as incrementalOrdersAll$,
  incrementalOrdersV2$ as incrementalOrdersV2All$,
  snapshotOrders$ as snapshotOrdersAll$,
  snapshotOrdersV2$ as snapshotOrdersV2All$,
} from '@/orders-contract/orders-contract-all';
import {qtyTypeBeforeV2$, qtyTypeV2$} from '@/qty-type-contract/subscription';
import type {ws, ReduceAction, ContractPair} from '@/types';

type ContractOpenRaw = ws.ContractOpenRaw;

const incrementalOrder$ = merge(
  incrementalOrders$,
  incrementalOrdersV2$,
  incrementalOrdersAll$,
  incrementalOrdersV2All$
).pipe(
  map(({orders: {open, closed}}) => [].concat(closed, open)),
  mergeAll()
);

const snapshotOrder$ = merge(snapshotOrders$, snapshotOrdersV2$, snapshotOrdersAll$, snapshotOrdersV2All$).pipe(
  map(({orders: {open, closed}}) => [].concat(closed, open))
);

const snapshotAction$ = snapshotOrder$.pipe(
  map((orders: ContractOpenRaw[]) => {
    return function (cache: Map<string, ContractOpenRaw>) {
      cache.clear();
      orders.forEach((o: ContractOpenRaw) => cache.set(o.orderID, o));
      return cache;
    };
  })
);

const updateAction$ = new Subject<ReduceAction<Map<string, ContractOpenRaw>>>();
const orderCache$ = new BehaviorSubject(new Map<string, ContractOpenRaw>());
const notification$ = new Subject();

export const orderNotification$ = notification$.asObservable();

merge(snapshotAction$, updateAction$)
  .pipe(scan((p, c) => c(p), new Map<string, ContractOpenRaw>()))
  .subscribe((cache) => orderCache$.next(cache));

incrementalOrder$.pipe(withLatestFrom(contracts$)).subscribe(([order, contracts]) => {
  const contract = contracts.find((o) => o.symbol === order.symbol);
  if (!contract) {
    return;
  }

  const cache = orderCache$.getValue();
  const notification = convertOrderNotification(order, contract, cache);
  if (notification) {
    notification$.next(notification);
  }
  updateAction$.next(convertUpdateAction(order));
});

function convertDisplayQty(contract, orderQty, price) {
  const qtyType$ = contract.type === 'Perpetual' ? qtyTypeBeforeV2$ : qtyTypeV2$;
  const qtyType = qtyType$.getValue();

  const displayMap = {
    [QtyType.Cont]: () => qtyUtil.toCont(contract, orderQty),
    [QtyType.Base]: () => qtyUtil.toValue(contract, orderQty, price),
    [QtyType.Coin]: () => qtyUtil.toCoin(contract, orderQty, price),
  };

  return displayMap[qtyType] && displayMap[qtyType]();
}

function convertOrderNotification(order, contract: ContractPair, cache) {
  const {pricePrecision, priceScale, pairName, settleCurrencyDisplay} = contract;
  const {
    side,
    orderQty,
    priceEp,
    stopPxEp,
    leavesQty,
    ordStatus,
    code,
    action,
    pegPriceType = 'Unspecified',
    curPosSide,
    stopDirection,
    clOrdID,
    ordType,
    execStatus,
    timeInForce,
  } = order;
  const isMarket = orderUtil.isMarketOrder(ordType);
  const isConditionalOrder = orderUtil.isConditionalOrder(ordType);
  const Price = isMarket ? 'Market' : numUtil.floor(priceEp, pricePrecision, priceScale);
  const QtyPrice = numUtil.floor(isMarket ? stopPxEp : priceEp, pricePrecision, priceScale);
  const Side = side;
  const OrderQty = orderQty;
  const Symbol = pairName;
  const SettleCurrency = settleCurrencyDisplay;

  // 分段订单提示信息在api中抛出
  if (clOrdID.startsWith('scaled-') && ordType === 'Limit' && action !== 'Replace') {
    return null;
  }

  if (code !== 0) {
    return {
      ID: 'OrderError',
      type: ['Canceled', 'Deactivated'].indexOf(ordStatus) > -1 ? 'info' : 'error',
      Code: code,
    };
  }

  if (action === 'Replace' && ['Replaced', 'ReAdded'].includes(execStatus)) {
    return convertReplaceNotification(order, contract, cache);
  }

  // 锚定仓位的提示
  if (orderQty === 0) {
    if ((action === 'Replace' || action === 'New') && ordStatus === 'Untriggered') {
      if (pegPriceType === 'Unspecified') {
        // 止盈设置成功
        if (stopDirection === 'Rising') {
          return {
            ID: curPosSide === 'Buy' ? 'TakeProfileAccepted' : 'StopLossAccepted',
            Price: numUtil.floor(order.stopPxEp, pricePrecision, priceScale),
          };
        }

        // 止损设置成功
        if (stopDirection === 'Falling') {
          return {
            ID: curPosSide === 'Buy' ? 'StopLossAccepted' : 'TakeProfileAccepted',
            Price: numUtil.floor(order.stopPxEp, pricePrecision, priceScale),
          };
        }
      }

      // 追踪止损设置成功
      if (pegPriceType === 'TrailingStopPeg') {
        return {
          ID: 'TrailingStopAccepted',
          Price: numUtil.floor(order.stopPxEp, pricePrecision, priceScale),
          TrailValue: numUtil.floor(Math.abs(order.pegOffsetValueEp), pricePrecision, priceScale),
        };
      }

      // 追踪止盈设置成功
      if (pegPriceType === 'TrailingTakeProfitPeg') {
        return {
          ID: 'TrailingProfitAccepted',
          Price: numUtil.floor(order.stopPxEp - order.pegOffsetValueEp, pricePrecision, priceScale),
          TrailValue: numUtil.floor(Math.abs(order.pegOffsetValueEp), pricePrecision, priceScale),
        };
      }
    }
  }

  // Deactivated, PartiallyFilled, Filled 与 action 无关, 因此在前面处理
  // 取消未触发的条件订单
  if (ordStatus === 'Deactivated') {
    return {
      ID: 'OrderCanceled',
    };
  }
  if (ordStatus === 'PartiallyFilled') {
    const Price = numUtil.floor(order.execPriceEp, pricePrecision, priceScale);
    return {
      ID: 'OrderPartiallyFilled',
      Symbol,
      SettleCurrency,
      Side,
      OrderQty: convertDisplayQty(contract, order.execQty, Price),
      Price,
      LeavesQty: leavesQty,
    };
  }

  if (ordStatus === 'Filled') {
    const price2 = numUtil.floor(order.priceEp, pricePrecision, priceScale);
    return {
      ID: 'OrderFilled',
      Symbol,
      SettleCurrency,
      Side,
      OrderQty: convertDisplayQty(contract, OrderQty, price2),
      Price,
    };
  }

  if (action === 'New') {
    if (isConditionalOrder && ordStatus === 'Untriggered') {
      return {
        ID: 'ConditionalOrderSubmitted',
        Symbol,
        SettleCurrency,
        Side,
        OrderQty: convertDisplayQty(contract, OrderQty, QtyPrice),
        Price,
        TriggerPrice: numUtil.floor(order.stopPxEp, pricePrecision, priceScale),
      };
    }

    if (isConditionalOrder && ordStatus === 'New') {
      return {
        ID: 'ConditionalOrderTriggered',
        Symbol,
        SettleCurrency,
        Side,
        OrderQty: convertDisplayQty(contract, OrderQty, QtyPrice),
        Price,
        StopDirection: stopDirection === 'Rising' ? 'profit' : 'stop',
      };
    }

    if (!isConditionalOrder && ordStatus === 'New') {
      return {
        ID: 'OrderSubmitted',
        Symbol,
        SettleCurrency,
        Side,
        OrderQty: convertDisplayQty(contract, OrderQty, QtyPrice),
        Price,
      };
    }
  }

  if (execStatus === 'Canceled') {
    return {
      ID: 'OrderCanceled',
    };
  }

  if (execStatus === 'Expired' && ordStatus === 'Canceled' && timeInForce === 'PostOnly') {
    return {
      ID: 'OrderCanceled',
    };
  }

  return null;
}

function convertUpdateAction(order) {
  if (orderUtil.isFinalOrder(order.ordStatus) && order.execStatus !== 'ReplaceToCanceled') {
    return function (cache) {
      cache.delete(order.orderID, order);
      return cache;
    };
  }

  return function (cache) {
    cache.set(order.orderID, order);
    return cache;
  };
}

function convertReplaceNotification(order, contract, cache) {
  const oldOrder = cache.get(order.orderID);
  if (!oldOrder) {
    return null;
  }

  const {pairName, settleCurrencyDisplay} = contract;
  const {
    orderQty,
    priceEp,
    stopPxEp,
    tpTimeInForce,
    tpTrigger,
    tpPxRp,
    takeProfitRp,
    slTimeInForce,
    slTrigger,
    slPxRp,
    stopLossRp,
  } = order;

  // 比较订单是否变化，模糊提示
  if (
    oldOrder.priceEp !== priceEp ||
    oldOrder.orderQty !== orderQty ||
    oldOrder.stopPxEp !== stopPxEp ||
    oldOrder.tpTimeInForce !== tpTimeInForce ||
    oldOrder.tpTrigger !== tpTrigger ||
    oldOrder.tpPxRp !== tpPxRp ||
    oldOrder.takeProfitRp !== takeProfitRp ||
    oldOrder.slTimeInForce !== slTimeInForce ||
    oldOrder.slTrigger !== slTrigger ||
    oldOrder.slPxRp !== slPxRp ||
    oldOrder.stopLossRp !== stopLossRp
  ) {
    return {
      ID: 'OrderModify',
      Symbol: pairName,
      SettleCurrency: settleCurrencyDisplay,
    };
  }

  return null;
}
